import { LanguageConfig, ReportMode } from '@/types';

// Language configurations for buyer vs seller modes
export const LANGUAGE_CONFIGS: Record<ReportMode, LanguageConfig> = {
  buyer: {
    mode: 'buyer',
    severityLabels: {
      safety_concern: 'Safety Concern',
      recommendation: 'Recommendation',
      observation: 'Observation',
    },
    defaultPhrases: {
      safety_concern: [
        'Immediate attention required',
        'Safety hazard identified',
        'Requires professional repair',
        'Significant structural concern',
      ],
      recommendation: [
        'Recommend repair before closing',
        'Should be addressed promptly',
        'May affect property value',
        'Professional evaluation recommended',
      ],
      observation: [
        'Noted during inspection',
        'Informational only',
        'No action required',
        'Documented for reference',
      ],
    },
  },
  seller: {
    mode: 'seller',
    severityLabels: {
      safety_concern: 'Safety Concern',
      recommendation: 'Suggested Improvement',
      observation: 'Observation',
    },
    defaultPhrases: {
      safety_concern: [
        'Attention recommended',
        'Consider professional consultation',
        'May benefit from repair',
        'Worth addressing for property condition',
      ],
      recommendation: [
        'Suggested maintenance item',
        'Consider repair for optimal condition',
        'Improvement opportunity noted',
        'May enhance property appeal',
      ],
      observation: [
        'Standard condition noted',
        'Typical wear observed',
        'Documented for reference',
        'No action required',
      ],
    },
  },
};

// Default report templates with expected checklist items per ASHI/InterNACHI standards
export const DEFAULT_TEMPLATES = {
  residential: {
    name: 'Residential Inspection',
    sections: [
      { title: 'Introduction', order: 0, expectedChecks: [] as string[] },
      { title: 'Roof', order: 1, expectedChecks: [
        'Shingles/covering', 'Flashing', 'Gutters/downspouts',
        'Skylights', 'Chimney', 'Penetrations (vents, pipes, stacks)',
        'Valleys', 'Drip edge',
      ]},
      { title: 'Attic', order: 2, expectedChecks: [
        'Insulation (type and depth)', 'Ventilation (soffit/ridge/gable)',
        'Framing/structure', 'Vapor barrier', 'Access hatch',
        'Exhaust terminations (bath/kitchen/dryer)',
        'Evidence of leaks or staining', 'Pests/rodent activity',
        'Electrical wiring and junction boxes',
      ]},
      { title: 'Exterior', order: 3, expectedChecks: [
        'Siding/cladding', 'Trim/fascia', 'Eaves/soffits',
        'Windows', 'Doors', 'Grading/drainage', 'Walkways/driveways',
        'Deck/porch', 'Stairs/railings', 'Retaining walls',
        'Exterior GFCI outlets', 'Hose bibs',
      ]},
      { title: 'Structural Components', order: 4, expectedChecks: [
        'Foundation', 'Floor structure', 'Wall structure', 'Ceiling structure',
        'Roof structure', 'Load-bearing walls',
      ]},
      { title: 'Electrical System', order: 5, expectedChecks: [
        'Service entrance', 'Main panel', 'Sub-panels', 'Branch wiring',
        'Outlets/switches', 'GFCI protection', 'AFCI protection', 'Smoke/CO detectors',
      ]},
      { title: 'Plumbing System', order: 6, expectedChecks: [
        'Supply lines', 'Drain lines', 'Water heater', 'Fixtures/faucets',
        'Toilets', 'Water pressure', 'Shut-off valves', 'Sump pump',
      ]},
      { title: 'HVAC System', order: 7, expectedChecks: [
        'Heating system', 'Cooling system', 'Thermostat', 'Ductwork',
        'Filters', 'Condensate drain', 'Fuel supply',
      ]},
      { title: 'Interior', order: 8, expectedChecks: [
        'Walls/ceilings', 'Floors', 'Doors', 'Windows',
        'Stairs/railings', 'Fireplace/chimney',
      ]},
      { title: 'Kitchen & Appliances', order: 9, expectedChecks: [
        'Cabinets', 'Countertops', 'Walls', 'Floors', 'Ceilings',
        'Lights/fixtures', 'GFCI outlets', 'Sink/faucet',
        'Dishwasher', 'Range/oven', 'Microwave', 'Refrigerator',
        'Disposal', 'Ventilation/exhaust',
      ]},
      { title: 'Bathrooms', order: 10, expectedChecks: [
        'Cabinets', 'Countertops', 'Windows', 'Flooring',
        'Sink/faucet', 'Toilet', 'Tub/shower', 'Ventilation',
        'Tile/grout', 'Caulking', 'GFCI outlets',
      ]},
      { title: 'Basement / Crawlspace / Slab', order: 11, expectedChecks: [
        'Foundation walls', 'Floor/slab', 'Moisture/water intrusion',
        'Insulation', 'Columns/beams', 'Crawl space access',
        'Vapor barrier', 'Sump pump', 'Radon mitigation (if present)',
      ]},
      { title: 'Garage', order: 12, expectedChecks: [
        'Firewall/fire separation (drywall integrity, self-closing door)',
        'Overhead garage door', 'Door opener (auto-reverse, photo eye)',
        'Service door to house', 'Receptacles and GFCI protection',
        'Floor/slab', 'Walls/ceiling', 'Stairs/railings',
      ]},
      { title: 'Summary & Recommendations', order: 13, expectedChecks: [] as string[] },
    ],
  },
  commercial: {
    name: 'Commercial Inspection',
    sections: [
      { title: 'Executive Summary', order: 0, expectedChecks: [] as string[] },
      { title: 'Building Exterior', order: 1, expectedChecks: [
        'Facade/cladding', 'Windows/glazing', 'Doors/entries', 'Loading docks',
        'Grading/drainage', 'Signage',
      ]},
      { title: 'Structural Systems', order: 2, expectedChecks: [
        'Foundation', 'Framing', 'Load-bearing walls', 'Floor systems',
        'Columns/beams',
      ]},
      { title: 'Roofing System', order: 3, expectedChecks: [
        'Membrane/covering', 'Flashing', 'Drainage', 'Penetrations',
        'Skylights', 'Roof access',
      ]},
      { title: 'Electrical Systems', order: 4, expectedChecks: [
        'Service entrance', 'Main distribution', 'Panels/breakers', 'Emergency power',
        'Lighting', 'Emergency lighting', 'Exit signs',
      ]},
      { title: 'Plumbing Systems', order: 5, expectedChecks: [
        'Supply lines', 'Drain/waste', 'Water heater', 'Fixtures',
        'Backflow prevention', 'Grease traps',
      ]},
      { title: 'HVAC Systems', order: 6, expectedChecks: [
        'Rooftop units', 'Split systems', 'Ductwork', 'Controls',
        'Ventilation', 'Exhaust systems',
      ]},
      { title: 'Fire Safety & Egress', order: 7, expectedChecks: [
        'Fire alarm system', 'Sprinkler system', 'Fire extinguishers', 'Emergency exits',
        'Exit signage', 'Fire doors',
      ]},
      { title: 'ADA Compliance', order: 8, expectedChecks: [
        'Accessible entrances', 'Ramps', 'Restrooms', 'Signage',
        'Parking', 'Door hardware',
      ]},
      { title: 'Interior Spaces', order: 9, expectedChecks: [
        'Walls/ceilings', 'Flooring', 'Doors/hardware', 'Restrooms',
        'Common areas', 'Tenant spaces',
      ]},
      { title: 'Site & Parking', order: 10, expectedChecks: [
        'Parking lot surface', 'Striping/markings', 'Curbing', 'Landscaping',
        'Fencing', 'Site lighting',
      ]},
      { title: 'Recommendations & Cost Estimates', order: 11, expectedChecks: [] as string[] },
    ],
  },
};

// File upload constraints
export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp'],
};

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  API: 'YYYY-MM-DD',
  API_WITH_TIME: 'YYYY-MM-DDTHH:mm:ss',
};

// Offline storage keys
export const STORAGE_KEYS = {
  OFFLINE_DRAFTS: 'flowspect_offline_drafts',
  USER_PREFERENCES: 'flowspect_user_preferences',
  CACHED_REPORTS: 'flowspect_cached_reports',
};

// Annotation colors
export const ANNOTATION_COLORS = [
  '#ef4444', // red
  '#f59e0b', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#ffffff', // white
];

// Email template types
export const EMAIL_TEMPLATES = {
  REMINDER: 'reminder',
  AGREEMENT: 'agreement',
  REVIEW_REQUEST: 'review_request',
  REPORT_READY: 'report_ready',
};

// Report statuses
export const REPORT_STATUSES = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SENT: 'sent',
} as const;

